import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown, Compass, Eye, EyeOff, HelpCircle, Loader2, Trash2, Volume2, VolumeX, X } from "lucide-react";
import { it } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { play as playUiSound, soundsEnabled, setSoundsEnabled, onSoundsChange } from "@/lib/uiSounds";

export function Card({ className = "", children, ...p }) {
  return (
    <div className={`bg-white border border-hairline shadow-card rounded-xl ${className}`} {...p}>
      {children}
    </div>
  );
}

const btnBase =
  "inline-flex max-w-full items-center justify-center gap-2 whitespace-normal text-center font-body font-medium rounded-lg transition-colors min-h-[44px] px-5 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed text-sm";

export function Button({ variant = "primary", className = "", loading, children, ...p }) {
  const styles = {
    primary: "bg-navy text-white hover:bg-navy-600",
    accent: "bg-gold text-white hover:bg-gold-light font-semibold",
    outline: "border border-navy text-navy hover:bg-slate-50",
    ghost: "text-navy hover:bg-slate-100",
    danger: "bg-haccp-error text-white hover:brightness-95",
    success: "bg-haccp-success text-white hover:brightness-95",
  }[variant];
  return (
    <button className={`${btnBase} ${styles} ${className}`} disabled={loading || p.disabled} {...p}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function ConfirmDeleteDialog({ open, title = "Confermare l’eliminazione?", description, confirmLabel = "Sì, elimina", busy = false, confirmDisabled = false, onCancel, onConfirm }) {
  useEffect(() => { if (open) playUiSound("alert"); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (event.key === "Escape" && !busy) onCancel?.(); };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open, busy, onCancel]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel?.(); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-delete-title" aria-describedby="confirm-delete-description" className="relative w-full max-w-lg rounded-2xl border border-red-100 bg-white p-6 shadow-2xl sm:p-8">
        <button type="button" disabled={busy} onClick={onCancel} className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-slate-500 hover:bg-slate-100 disabled:opacity-50" aria-label="Chiudi e annulla"><X className="h-5 w-5" /></button>
        <div className="mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-600"><Trash2 className="h-7 w-7" /></div>
        <h2 id="confirm-delete-title" className="pr-10 font-heading text-2xl font-bold text-navy">{title}</h2>
        <div id="confirm-delete-description" className="mt-3 leading-relaxed text-slate-600">{description}</div>
        <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Annulla</Button>
          <Button type="button" variant="danger" disabled={confirmDisabled} loading={busy} onClick={onConfirm}><Trash2 className="h-4 w-4" />{confirmLabel}</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function SoundToggle({ className = "", showLabel = false, testid = "sound-toggle" }) {
  const [on, setOn] = useState(soundsEnabled);
  useEffect(() => onSoundsChange(() => setOn(soundsEnabled())), []);
  const label = on ? "Suoni attivi" : "Suoni disattivati";
  const Icon = on ? Volume2 : VolumeX;
  return (
    <button type="button" data-sound="off" onClick={() => setSoundsEnabled(!on)} className={className}
      aria-pressed={on} aria-label={showLabel ? undefined : `${label}: tocca per ${on ? "disattivarli" : "attivarli"}`}
      title={label} data-testid={testid}>
      <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} />
      {showLabel && label}
    </button>
  );
}

export function FieldHelp({ text }) {
  if (!text) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="inline-grid h-5 w-5 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
          aria-label="Aiuto per questo campo"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-72 max-w-[calc(100vw-24px)] whitespace-normal border border-hairline bg-white text-sm normal-case tracking-normal text-slate-600 shadow-xl font-body"
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

export function Field({ label, hint, help, children, htmlFor, required }) {
  const isRequired = required ?? (typeof label === "string" && label.includes("*"));
  const control = isRequired && React.isValidElement(children)
    ? React.cloneElement(children, { required: true, "data-required-field": "true" })
    : children;
  return (
    // L'etichetta avvolge testo + campo: è questo (non solo l'affiancamento
    // visivo) a collegarli davvero per chi usa uno screen reader, anche
    // quando il campo non ha un id esplicito da abbinare con htmlFor.
    <label htmlFor={htmlFor} className="block">
      {(label || help) && (
        <div className="mb-1.5 flex items-center gap-1.5">
          {label && (
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 font-body">
              {label}
            </span>
          )}
          <FieldHelp text={help} />
        </div>
      )}
      {control}
      {hint && <span className="block mt-1 text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

export const inputCls =
  "w-full min-h-[44px] px-4 py-2.5 rounded-lg border border-hairline bg-white text-navy font-body text-sm placeholder:text-slate-400 focus:border-gold required:invalid:border-red-500 required:invalid:bg-red-50/40 required:invalid:ring-1 required:invalid:ring-red-200 required:valid:border-emerald-500 required:valid:bg-emerald-50/30 required:valid:ring-1 required:valid:ring-emerald-200";

export function Input({ className = "", ...props }) {
  return <input {...props} className={`${inputCls} ${className}`} />;
}

// Campo password con occhio: mostra solo ciò che si sta digitando (le password salvate sono hash, non recuperabili).
export function PasswordInput({ className = "", ...props }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <div className="relative">
      <input {...props} type={visible ? "text" : "password"} className={`${inputCls} pr-12 ${className}`} />
      <button type="button" onClick={() => setVisible((v) => !v)} className="absolute right-1.5 top-1/2 grid min-h-[40px] min-w-[40px] -translate-y-1/2 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label={visible ? "Nascondi password" : "Mostra password"} aria-pressed={visible}>
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

const formatItalianDateInput = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join("-");
};
const isoToItalianDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return value || "";
  const [year, month, day] = value.split("-");
  return `${day}-${month}-${year}`;
};
const italianToIsoDate = (value) => {
  if (!/^\d{2}-\d{2}-\d{4}$/.test(value || "")) return "";
  const [day, month, year] = value.split("-");
  const candidate = new Date(`${year}-${month}-${day}T00:00:00`);
  if (Number.isNaN(candidate.getTime()) || candidate.getFullYear() !== Number(year) || candidate.getMonth() + 1 !== Number(month) || candidate.getDate() !== Number(day)) return "";
  return `${year}-${month}-${day}`;
};

export function DateInput({ value = "", onChange, valueMode = "italian", validateFormat = false, className = "", disabled = false, required = false, "aria-label": ariaLabel, ...props }) {
  const externalDisplay = valueMode === "iso" ? isoToItalianDate(value) : value;
  const [displayValue, setDisplayValue] = useState(externalDisplay || "");
  const [calendarOpen, setCalendarOpen] = useState(false);
  useEffect(() => { setDisplayValue(externalDisplay || ""); }, [externalDisplay]);
  const emit = (nextDisplay, selectedIso = "") => {
    const nextValue = valueMode === "iso" ? (selectedIso || italianToIsoDate(nextDisplay)) : nextDisplay;
    if (nextValue || !nextDisplay) onChange?.({ target: { value: nextValue } });
  };
  const selectFromCalendar = (event) => {
    const iso = event.target.value;
    const italian = isoToItalianDate(iso);
    setDisplayValue(italian);
    emit(italian, iso);
  };
  const invalidFormat = (validateFormat || required) && !disabled && !italianToIsoDate(displayValue);
  const dateStateClass = disabled
    ? ""
    : invalidFormat
      ? "!border-red-500 !bg-red-50 !ring-1 !ring-red-300"
      : required || validateFormat
        ? "!border-emerald-500 !bg-emerald-50/30 !ring-1 !ring-emerald-200"
        : "";
  const selectedIso = valueMode === "iso" ? value : italianToIsoDate(displayValue);
  const selectedDate = selectedIso ? new Date(`${selectedIso}T12:00:00`) : undefined;
  return <div>
    <div className="relative">
      <input {...props} disabled={disabled} required={required} pattern="\d{2}-\d{2}-\d{4}" type="text" inputMode="numeric" maxLength={10} placeholder="GG-MM-AAAA" aria-label={ariaLabel || "Data nel formato giorno-mese-anno"} aria-invalid={invalidFormat || undefined} value={displayValue} onChange={(event) => { const formatted = formatItalianDateInput(event.target.value); setDisplayValue(formatted); emit(formatted); }} className={`${inputCls} pr-14 ${dateStateClass} ${className}`} />
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <button disabled={disabled} type="button" aria-label={`Apri calendario: ${ariaLabel || "seleziona data"}`} title="Scegli la data dal calendario" className="absolute right-px top-px grid h-[42px] w-11 place-items-center rounded-r-lg text-navy transition-colors hover:bg-blue-50 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:cursor-not-allowed disabled:opacity-50">
            <CalendarDays className="h-5 w-5" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" collisionPadding={12} className="w-auto max-w-[calc(100vw-24px)] rounded-xl border-hairline bg-white p-2 shadow-xl">
          <Calendar mode="single" locale={it} selected={selectedDate} defaultMonth={selectedDate} onSelect={(date) => { if (!date) return; selectFromCalendar({ target: { value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` } }); setCalendarOpen(false); }} initialFocus classNames={{ month: "space-y-3", caption_label: "text-sm font-semibold capitalize text-navy", head_cell: "w-10 text-center text-xs font-medium text-slate-500", row: "mt-1 flex w-full", cell: "relative p-0 text-center", day: "h-10 w-10 rounded-lg p-0 text-sm font-medium text-navy hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold", day_selected: "bg-navy text-white hover:bg-navy hover:text-white", day_today: "ring-1 ring-gold", day_outside: "text-slate-300", day_disabled: "text-slate-300 opacity-50" }} />
        </PopoverContent>
      </Popover>
    </div>
    {invalidFormat && <span className="mt-1 block text-xs font-medium text-red-600" role="alert">Inserisci una data valida nel formato GG-MM-AAAA</span>}
  </div>;
}

export function DateTimeInput({ value = "", onChange, disabled = false, required = false, className = "", "aria-label": ariaLabel }) {
  const [date = "", time = ""] = String(value).split("T");
  const emit = (nextDate, nextTime) => onChange?.({ target: { value: nextDate && nextTime ? `${nextDate}T${nextTime}` : "" } });
  return <div className={`grid grid-cols-1 gap-2 min-[420px]:grid-cols-[minmax(0,1fr)_128px] ${className}`}>
    <DateInput valueMode="iso" validateFormat required={required} disabled={disabled} value={date} onChange={(event) => emit(event.target.value, time || "00:00")} aria-label={`${ariaLabel || "Data e ora"}: data`} />
    <input disabled={disabled} required={required} type="time" value={time} onChange={(event) => emit(date, event.target.value)} aria-label={`${ariaLabel || "Data e ora"}: ora`} className={inputCls} />
  </div>;
}

export function Select({ children, className = "", ...p }) {
  return <select {...p} className={`${inputCls} ${className}`}>{children}</select>;
}

const SEM = {
  verde: { bg: "bg-emerald-50", ring: "ring-emerald-200", dot: "bg-emerald-500", text: "text-emerald-700", label: "Conforme" },
  giallo: { bg: "bg-amber-50", ring: "ring-amber-200", dot: "bg-amber-500", text: "text-amber-700", label: "Da completare" },
  rosso: { bg: "bg-red-50", ring: "ring-red-200", dot: "bg-red-500", text: "text-red-700", label: "Non conforme" },
  grigio: { bg: "bg-slate-100", ring: "ring-slate-200", dot: "bg-slate-400", text: "text-slate-500", label: "Non operativo" },
  blu: { bg: "bg-blue-50", ring: "ring-blue-200", dot: "bg-blue-500", text: "text-blue-700", label: "Programmato" },
};

export function StatusDot({ status }) {
  const s = SEM[status] || SEM.grigio;
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.dot}`} />;
}

export function StatusBadge({ status, children, testid, className = "" }) {
  const s = SEM[status] || SEM.grigio;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${s.bg} ${s.ring} ${s.text} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {children || s.label}
    </span>
  );
}

export function PageGuide({ title = "Guida rapida", summary, goals = [], steps = [], className = "", testid }) {
  const [open, setOpen] = useState(true);
  if (!summary && goals.length === 0 && steps.length === 0) return null;
  return (
    <div className={`rounded-xl border border-blue-200 bg-blue-50/60 ${className}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 font-heading text-sm font-semibold text-navy"><Compass className="h-4 w-4 shrink-0 text-blue-600" /> {title}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-blue-100 px-4 py-3 text-sm leading-relaxed text-slate-700">
          {summary && <p>{summary}</p>}
          {goals.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">Cosa puoi ottenere qui</div>
              <ul className="list-disc space-y-1 pl-4">{goals.map((g, i) => <li key={i}>{g}</li>)}</ul>
            </div>
          )}
          {steps.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">Come compilare i campi</div>
              <ol className="list-decimal space-y-1 pl-4">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-20 text-slate-400">
      <Loader2 className="w-7 h-7 animate-spin" />
    </div>
  );
}

export { SEM };
